import React, { useState } from 'react';
import { Box, Drawer, IconButton, Typography, ListItem, ListItemIcon, ListItemText, MenuItem, Switch, SelectChangeEvent, Tooltip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CookieIcon from '@mui/icons-material/Cookie';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/i18n';
import { DrawerContent, CategoryBox, TileButton, BottomMenu, SettingsContainer, LanguageSelect } from './styles';
import { getIconForTile, renderIcon, TileData as BaseTileData } from '../../../config/tilesConfig';
import { impressumUrl, datenschutzUrl, translation, sharedState, logoutUrl } from '../../../config/config';
import Impressum from "../../../assets/icons/impressum";
import Datenschutz from "../../../assets/icons/datenschutz";
import Sun from '../../../assets/icons/sun';
import Moon from '../../../assets/icons/moon';
import UIWhite from '../../../assets/img/logo-ui-square-white.png';
import UIDark from '../../../assets/img/logo-ui-square-dark.png';
import { DoNotDisturbAlt, Share } from '@mui/icons-material';
import { useGeneralInfoStore } from '../../../stores/GeneralInfoStore';
import Logout from '../../../assets/icons/demoIcons/logout';

type TileData = BaseTileData & { title: string };

interface NavigationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    darkMode: string;
    toggleDarkMode: () => void;
    routesConfig: { [key: string]: boolean };
    translatedTiles: TileData[];
    onTileSelect: (tile: TileData) => void;
    checkedColor: string;
    trackColor: string;
    iconColor: string;
    logoColor: string;
    isCookieBannerVisible: boolean;
    setIsCookieBannerVisible: (visible: boolean) => void;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
    isOpen,
    onClose,
    darkMode,
    toggleDarkMode,
    routesConfig,
    translatedTiles,
    onTileSelect,
    checkedColor,
    trackColor,
    iconColor,
    logoColor,
    isCookieBannerVisible,
    setIsCookieBannerVisible
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [languageError, setLanguageError] = useState<string>('');
    const { sharedVariable, toggleSharedVariable } = useGeneralInfoStore();

    const getActiveCategories = () => {
        return ['traffic', 'environment', 'energy', 'utility'].filter(cat =>
            translatedTiles.some(tile => tile.categories.includes(cat) && routesConfig[tile.id])
        );
    };

    const handleLanguageChange = async (event: SelectChangeEvent<unknown>) => {
        try {
            const newLang = event.target.value as string;
            await i18n.changeLanguage(newLang);
            setLanguageError('');
        } catch (error) {
            setLanguageError(t('errors.languageChange'));
            console.error('Language change error:', error);
        }
    };

    const toggleSharedVariableSwitch = () => {
        toggleSharedVariable();
    };

    const handleCookieSettings = () => {
        setIsCookieBannerVisible(!isCookieBannerVisible);
        onClose();
    };

    const renderTilesByCategory = () => {
        const activeCategories = getActiveCategories();

        return activeCategories.map(cat => {
            const categoryTiles = translatedTiles.filter(
                tile => tile.categories.includes(cat) && routesConfig[tile.id]
            );

            if (categoryTiles.length === 0) return null;

            return (
                <CategoryBox key={cat}>
                    <Typography className="category-title" color="textPrimary">
                        {renderIcon(getIconForTile(cat), { color: logoColor, size: 24 })}
                        {t(`sidebar.categories.${cat}`)}
                    </Typography>
                    <div className="tiles-grid">
                        {categoryTiles.map(tile => (
                            <TileButton
                                key={tile.id}
                                onClick={() => onTileSelect(tile)}
                            >
                                <div className="tile-icon">
                                    {renderIcon(getIconForTile(tile.id), { color: logoColor, size: 32 })}
                                </div>
                                <div className="tile-text-container">
                                    <Typography
                                        className="tile-text"
                                        color="textPrimary"
                                    >
                                        {tile.title}
                                    </Typography>
                                </div>
                            </TileButton>
                        ))}
                    </div>
                </CategoryBox>
            );
        });
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: '100%',
                    height: '100%',
                    background: theme.palette.mode === 'dark'
                        ? 'rgba(0, 0, 0, 0.5)'
                        : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '-webkit-backdrop-filter': 'blur(10px)',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'transparent'
                }
            }}
        >
            <DrawerContent>
                <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: logoColor }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <SettingsContainer>
                    {translation && (
                        <LanguageSelect
                            value={i18n.language}
                            onChange={handleLanguageChange}
                            size="small"
                            error={!!languageError}
                        >
                            <MenuItem value="de">{t('languages.german')}</MenuItem>
                            <MenuItem value="en">{t('languages.english')}</MenuItem>
                        </LanguageSelect>
                    )}
                    {languageError && (
                        <Typography color="error" variant="caption">
                            {languageError}
                        </Typography>
                    )}
                    {sharedState && (
                        <>
                            <Tooltip title="Dieser Schalter aktiviert, dass alle Standorte in allen Diagrammen mit der Karte geteilt werden.">
                                <Switch
                                    checked={sharedVariable}
                                    onChange={toggleSharedVariableSwitch}
                                    sx={{
                                        marginTop: '8px',
                                        width: '66px',
                                        borderRadius: '30px',
                                        '& .MuiSwitch-switchBase': {
                                            margin: '6px',
                                            padding: '6px',
                                            '&.Mui-checked': {
                                                color: checkedColor,
                                                '& + .MuiSwitch-track': {
                                                    backgroundColor: trackColor,
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                        '& .MuiSwitch-thumb': {
                                            width: '20px',
                                            height: '20px',
                                        },
                                        '& .MuiSwitch-track': {
                                            boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
                                            backgroundColor: trackColor,
                                            width: '120px',
                                            height: '20px',
                                            borderRadius: '30px',
                                            opacity: 1,
                                        },
                                    }}
                                    checkedIcon={
                                        <Share
                                            sx={{ fontSize: '16px', margin: '3px', color: iconColor }}
                                        />
                                    }
                                    icon={
                                        <DoNotDisturbAlt
                                            sx={{ fontSize: '16px', margin: '3px', color: iconColor }}
                                        />
                                    }
                                />
                            </Tooltip>
                        </>
                    )}

                    <Switch
                        checked={darkMode === 'light'}
                        onChange={toggleDarkMode}
                        sx={{
                            width: '66px',
                            borderRadius: '30px',
                            '& .MuiSwitch-switchBase': {
                                margin: '6px',
                                padding: '6px',
                                '&.Mui-checked': {
                                    color: checkedColor,
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: trackColor,
                                        opacity: 1,
                                    },
                                },
                            },
                            '& .MuiSwitch-thumb': {
                                width: '20px',
                                height: '20px',
                            },
                            '& .MuiSwitch-track': {
                                boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.2)',
                                backgroundColor: trackColor,
                                width: '120px',
                                height: '20px',
                                borderRadius: '30px',
                                opacity: 1,
                            },
                        }}
                        checkedIcon={<Sun color={iconColor} size={18} />}
                        icon={<Moon color={iconColor} size={20} />}
                    />
                </SettingsContainer>

                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '0.4em'
                    },
                    '&::-webkit-scrollbar-track': {
                        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                        webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.1)',
                        borderRadius: '16px',
                    }
                }}>
                    {renderTilesByCategory()}
                </Box>

                <BottomMenu>
                    <div className="list-items">
                        <ListItem button component="a" href={impressumUrl} target="_blank">
                            <ListItemIcon sx={{ color: logoColor }}>
                                <Impressum size={24} color={theme.palette.primary.main} />
                            </ListItemIcon>
                            <ListItemText primary={t('sidebar.links.imprint')} />
                        </ListItem>

                        <ListItem button component="a" href={datenschutzUrl} target="_blank">
                            <ListItemIcon sx={{ color: logoColor }}>
                                <Datenschutz size={24} color={theme.palette.primary.main} />
                            </ListItemIcon>
                            <ListItemText primary={t('sidebar.links.privacy')} />
                        </ListItem>

                        <ListItem button onClick={handleCookieSettings}>
                            <ListItemIcon sx={{ color: logoColor }}>
                                <CookieIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('sidebar.links.cookies')} />
                        </ListItem>
                        <ListItem button onClick={() => {
                            window.location.href = logoutUrl;
                        }}>
                            <ListItemIcon sx={{ color: logoColor }}>
                                <Logout size={24} color={logoColor} />
                            </ListItemIcon>
                            <ListItemText primary={t('sidebar.links.logout')} />
                        </ListItem>
                    </div>
                    <img
                        alt={t('sidebar.logoAlt')}
                        width={100}
                        src={darkMode === "dark" ? UIWhite : UIDark}
                    />
                </BottomMenu>
            </DrawerContent>
        </Drawer>
    );
};

export default NavigationDrawer;